/**
 * useNotification Hook
 *
 * Provides a simple interface for displaying toast notifications using MUI Snackbar.
 * Supports different severity levels (error, warning, info, success) and configurable
 * auto-hide behavior.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { showError, showSuccess } = useNotification();
 *
 *   const handleSave = async () => {
 *     try {
 *       await saveData();
 *       showSuccess("Data saved successfully!");
 *     } catch (error) {
 *       showError("Failed to save data");
 *     }
 *   };
 *
 *   return <button onClick={handleSave}>Save</button>;
 * }
 * ```
 */

import { Alert, AlertColor, Snackbar } from "@mui/material";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

interface NotificationConfig {
  message: string;
  severity: AlertColor;
  autoHideDuration?: number | null;
}

interface NotificationState extends NotificationConfig {
  open: boolean;
}

interface NotificationContextValue {
  showNotification: (config: NotificationConfig) => void;
  showError: (message: string, autoHide?: boolean) => void;
  showWarning: (message: string, autoHide?: boolean) => void;
  showInfo: (message: string, autoHide?: boolean) => void;
  showSuccess: (message: string, autoHide?: boolean) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined,
);

interface NotificationProviderProps {
  children: React.ReactNode;
}

/**
 * NotificationProvider
 *
 * Wrap your app or component tree with this provider to enable notifications.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <NotificationProvider>
 *       <YourApp />
 *     </NotificationProvider>
 *   );
 * }
 * ```
 */
export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notification, setNotification] = useState<NotificationState>({
    open: false,
    message: "",
    severity: "info",
    autoHideDuration: 6000,
  });

  const showNotification = useCallback((config: NotificationConfig) => {
    setNotification({
      open: true,
      message: config.message,
      severity: config.severity,
      autoHideDuration: config.autoHideDuration ?? 6000,
    });
  }, []);

  const showError = useCallback(
    (message: string, autoHide = false) => {
      showNotification({
        message,
        severity: "error",
        autoHideDuration: autoHide ? 6000 : null, // null means no auto-hide (requires manual dismissal)
      });
    },
    [showNotification],
  );

  const showWarning = useCallback(
    (message: string, autoHide = true) => {
      showNotification({
        message,
        severity: "warning",
        autoHideDuration: autoHide ? 6000 : null,
      });
    },
    [showNotification],
  );

  const showInfo = useCallback(
    (message: string, autoHide = true) => {
      showNotification({
        message,
        severity: "info",
        autoHideDuration: autoHide ? 6000 : null,
      });
    },
    [showNotification],
  );

  const showSuccess = useCallback(
    (message: string, autoHide = true) => {
      showNotification({
        message,
        severity: "success",
        autoHideDuration: autoHide ? 6000 : null,
      });
    },
    [showNotification],
  );

  const handleClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      setNotification((prev) => {
        // Don't close on clickaway for errors (they require explicit dismissal)
        if (reason === "clickaway" && prev.severity === "error") {
          return prev;
        }
        return { ...prev, open: false };
      });
    },
    [],
  );

  const contextValue: NotificationContextValue = useMemo(
    () => ({
      showNotification,
      showError,
      showWarning,
      showInfo,
      showSuccess,
    }),
    [showNotification, showError, showWarning, showInfo, showSuccess],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ position: "fixed", zIndex: 1400, maxWidth: "50%" }}
      >
        <Alert
          onClose={handleClose}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

/**
 * useNotification Hook
 *
 * Access notification functions to display toast messages.
 *
 * @returns Notification context with helper functions
 * @throws Error if used outside of NotificationProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { showError, showSuccess } = useNotification();
 *
 *   return (
 *     <button onClick={() => showSuccess("Operation completed!")}>
 *       Click me
 *     </button>
 *   );
 * }
 * ```
 */
export const useNotification = (): NotificationContextValue => {
  const context = useContext(NotificationContext);

  if (context === undefined) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }

  return context;
};
